import type { ThunkAction, ThunkDispatch } from 'redux-thunk';

/**
 * Action types for the Costing Template library slice.
 *
 * The STRING VALUES are byte-identical to the JavaScript original
 * (`Pages/Library/CostingTemplate/store/CostingTemplate.type.js`). This slice is still mounted under
 * the same `CostingTemplateData` key, and 18 files outside this module dispatch the JavaScript
 * thunks against it, so a changed string would silently stop reaching the reducer.
 *
 * `UPDATE_NEW_CREATED_COSTING_TEMPLATE_LIST` and `COPY_DATA` are handled by the reducer but are
 * dispatched by nothing. They are kept so the parity test can drive every declared type; treat them
 * as dead branches rather than as API.
 */
const CostingTemplateType = {
  ADD_COSTING_MODAL_CONTROL: 'ADD_COSTING_MODAL_CONTROL',
  SET_COSTING_TEMPLATE_LIST: 'SET_COSTING_TEMPLATE_LIST',
  UPDATE_NEW_CREATED_COSTING_TEMPLATE_LIST: 'UPDATE_NEW_CREATED_COSTING_TEMPLATE_LIST',
  SET_COSTING_TEMPLATE_DETAIL_BY_ID: 'SET_COSTING_TEMPLATE_DETAIL_BY_ID',
  COSTING_TEMPLATE_UNMOUNT: 'COSTING_TEMPLATE_UNMOUNT',
  ADDITIONAL_MODAL: 'ADDITIONAL_MODAL',
  LANDEDINFO_MODAL: 'LANDEDINFO_MODAL',
  UPDATE_COLUMNDATA: 'UPDATE_COLUMNDATA',
  REMOVE_COSTINGTEMPLATE: 'REMOVE_COSTINGTEMPLATE',
  SEARCH_COLUMN: 'SEARCH_COLUMN',
  REMOVE_COSTINGTEMPLATE_COLUMN: 'REMOVE_COSTINGTEMPLATE_COLUMN',
  EDIT_ADDITIONAL_DATA: 'EDIT_ADDITIONAL_DATA',
  EDIT_LANDED_DATA: 'EDIT_LANDED_DATA',
  COPY_DATA: 'COPY_DATA',
  SET_COSTING_CUSTOM_SECTION_LIST: 'SET_COSTING_CUSTOM_SECTION_LIST',
  SET_COSTING_CUSTOM_COLUMN_LIST: 'SET_COSTING_CUSTOM_COLUMN_LIST',
  SET_IDS_ADDITIONAL_AND_LANDED: 'SET_IDS_ADDITIONAL_AND_LANDED',
  ADDITIONAL_ROW: 'ADDITIONAL_ROW',
  LANDED_ROW: 'LANDED_ROW',
  DELETE_UNSAVED_LANDED_ROW: 'DELETE_UNSAVED_LANDED_ROW',
  DELETE_UNSAVED_ADDITIONAL_ROW: 'DELETE_UNSAVED_ADDITIONAL_ROW',
} as const;

export default CostingTemplateType;

/** The envelope every costing-template service resolves to. */
export interface ApiResponse<T> {
  code?: number;
  message?: string;
  data?: T;
}

/** The error payload an envelope carries on a non-200 code. */
export interface ApiErrorData {
  errMsg?: string;
}

/** The i18n `t` function, passed in by callers as `transactionMsg`. */
export type TransactionMsg = (key: string) => string;

/**
 * Anything the module displays by `name`, keyed by `_id`.
 *
 * Both fields are `?: T | undefined` rather than plain optional because `exactOptionalPropertyTypes`
 * is on and the API genuinely returns references with the name absent — `joinNames` exists precisely
 * to filter those out. The plain optional form would reject assigning the shape that actually
 * arrives.
 */
export interface NamedRef {
  _id?: string | undefined;
  name?: string | undefined;
  [key: string]: unknown;
}

/**
 * One column on a costing template, in any of its four sections.
 *
 * Deliberately loose: the four sections carry overlapping but not identical field sets, and unsaved
 * draft rows add `_tempId` while lacking `_id`. The index signature is what lets one type serve all
 * four without inventing a discriminant the server does not send.
 */
export interface CostingTemplateColumn {
  _id?: string;
  _tempId?: number;
  title?: string;
  type?: string;
  key?: string;
  aliasDisplayName?: string;
  tempValue?: string;
  position?: number;
  isActive?: boolean;
  isDefault?: boolean;
  [key: string]: unknown;
}

/** A costing template as the list and the column editor read it. */
export interface CostingTemplateDetail {
  _id?: string;
  name?: string;
  vat?: unknown;
  isSetUpCostActive?: boolean;
  isAdditionalComponentActive?: boolean;
  costingInfo?: CostingTemplateColumn[];
  additionalInfo?: CostingTemplateColumn[];
  additionalComponentInfo?: CostingTemplateColumn[];
  landedInfo?: CostingTemplateColumn[];
  specificationRequirementInfo?: CostingTemplateColumn[];
  specificationRequirementTitle?: string;
  isSpecificationRequirementActive?: boolean;
  [key: string]: unknown;
}

/** The ids selected for bulk delete, split by section. */
export interface SelectedColumnIds {
  additonalIds: string[];
  landedIds: string[];
}

/**
 * The paged list payload. `isScrolled` distinguishes an append from a replace, and `searchText`
 * makes a search result replace rather than append — see `setArrayByCase` in the reducer.
 */
export interface CostingTemplateListPayload {
  data: CostingTemplateDetail[];
  totalCount?: number;
  searchText?: string;
  isScrolled?: boolean;
}

/** The slice, mounted as `CostingTemplateData`. */
export interface CostingTemplateState {
  costingModalState: boolean;
  costingTemplateList: CostingTemplateDetail[];
  costingTemplateListCount: number;
  costingTemplateDetail: CostingTemplateDetail | null;
  additionalModalState: boolean;
  landedInfoModalState: boolean;
  searchCostingColumnValue: string;
  updateAdditionalInfoData: CostingTemplateColumn | null;
  updateLandedInfoData: CostingTemplateColumn | null;
  customColumnList: NamedRef[];
  customList: NamedRef[];
  selectedIds: SelectedColumnIds;
  additionalRow: CostingTemplateColumn[];
  landedRow: CostingTemplateColumn[];
}

/** Which section a column belongs to, as the remove-column action names it. */
export interface RemoveColumnPayload {
  type: 'costingInfo' | 'additionalInfo' | 'additionalComponentInfo' | 'landedInfo' | 'specificationRequirementInfo';
  columnId: string;
}

export type CostingTemplateAction =
  | { type: typeof CostingTemplateType.ADD_COSTING_MODAL_CONTROL; payload: boolean }
  | { type: typeof CostingTemplateType.SET_COSTING_TEMPLATE_LIST; payload: CostingTemplateListPayload }
  | { type: typeof CostingTemplateType.UPDATE_NEW_CREATED_COSTING_TEMPLATE_LIST; payload: { data: CostingTemplateDetail[] } }
  | { type: typeof CostingTemplateType.SET_COSTING_TEMPLATE_DETAIL_BY_ID; payload: CostingTemplateDetail | null }
  | { type: typeof CostingTemplateType.UPDATE_COLUMNDATA; payload: CostingTemplateDetail | null }
  | { type: typeof CostingTemplateType.COSTING_TEMPLATE_UNMOUNT }
  | { type: typeof CostingTemplateType.ADDITIONAL_MODAL; payload: boolean }
  | { type: typeof CostingTemplateType.LANDEDINFO_MODAL; payload: boolean }
  | { type: typeof CostingTemplateType.REMOVE_COSTINGTEMPLATE; payload: string }
  | { type: typeof CostingTemplateType.SEARCH_COLUMN; payload: string }
  | { type: typeof CostingTemplateType.REMOVE_COSTINGTEMPLATE_COLUMN; payload: RemoveColumnPayload }
  | { type: typeof CostingTemplateType.EDIT_ADDITIONAL_DATA; payload: CostingTemplateColumn | null }
  | { type: typeof CostingTemplateType.EDIT_LANDED_DATA; payload: CostingTemplateColumn | null }
  | { type: typeof CostingTemplateType.COPY_DATA; payload: CostingTemplateDetail }
  | { type: typeof CostingTemplateType.SET_COSTING_CUSTOM_SECTION_LIST; payload: NamedRef[] }
  | { type: typeof CostingTemplateType.SET_COSTING_CUSTOM_COLUMN_LIST; payload: NamedRef[] }
  | { type: typeof CostingTemplateType.SET_IDS_ADDITIONAL_AND_LANDED; payload: SelectedColumnIds }
  | { type: typeof CostingTemplateType.ADDITIONAL_ROW; payload: CostingTemplateColumn }
  | { type: typeof CostingTemplateType.LANDED_ROW; payload: CostingTemplateColumn }
  | { type: typeof CostingTemplateType.DELETE_UNSAVED_LANDED_ROW; payload: number }
  | { type: typeof CostingTemplateType.DELETE_UNSAVED_ADDITIONAL_ROW; payload: number };

/*
 * State is `unknown` rather than a concrete root state because no thunk in this slice reads
 * getState, and a concrete state type makes the dispatch generic contravariantly incompatible with
 * the untyped .js thunks these components also dispatch. Same reasoning as ProductCostingTSX.
 */
export type CostingTemplateThunkAction<R = void> = ThunkAction<R, unknown, unknown, CostingTemplateAction>;
export type CostingTemplateDispatch = ThunkDispatch<unknown, unknown, CostingTemplateAction>;
